import {
  cloneElement,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import s from './ui.module.css';

export { Combobox, Select, type DropdownChange } from './Dropdown';

/*
 * Form fields, apart from ui.tsx so the public marketing pages can use them without the router (ui.tsx's ButtonLink
 * imports react-router). ui.tsx re-exports everything here.
 */

/** A labelled control. The child input gets the id, hint and error wiring. */
export function Field({ label, hint, error, children }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactElement }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id,
        'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
        'aria-invalid': error ? true : undefined,
      })
    : children;
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={id}>
        {label}
      </label>
      {control}
      {hint && (
        <span className={s.hint} id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className={s.error} id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}

export const Input = (props: InputHTMLAttributes<HTMLInputElement>) => <input className={s.input} {...props} />;
export const Textarea = (props: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className={s.textarea} {...props} />;
